import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation, useParams } from 'react-router-dom';

import Breadcrumbs from '../../components/ui/Breadcrumbs';
import Tabs from '../../components/ui/Tabs';
import Button from '../../components/ui/Button';
import { Grid, State } from '../performance-operations/styles';
import { AiSummaryPanel, EvidenceDrawer, ProfileHeader } from './components/ProfileParts';
import {
  CompetenciesTab, DevelopmentTab, GoalsTab, HistoryTab, MeetingsTab, ReviewsTab, SummaryTab, TalentTab,
} from './tab-pages/ProfileTabs';
import {
  fetchProfile, profileTabs, safeFrom, summarizeProfile,
} from './config';

// P7a — E14 Çalışan performans profili (/performance/profile/:employeeId?periodId=&from=). Yetkili yönetici (kişi kümesi) ve İK
// için tek kişinin dönem performansının bütünü: üst alan (çalışan, dönem organizasyonu, yönetici, sonuç durumu; M52/M53/M54),
// sekmeler (özet, hedefler, yetkinlikler, değerlendirmeler, görüşmeler, gelişim, yetenek, geçmiş), son 3 dönem grafiği, açık görev
// ve gelişim aksiyonları, yetkili sonuç değişikliği geçmişi, sağ çekmecede kanıt/hesaplama dökümü. AI alanı yönetici için görüşme
// hazırlığı/özet üretir (AI Plus; sayı doğrulamasından geçmeyen özet gösterilmez). Kendi profili bu sayfada açılmaz (E18).
// Kaynak ekranın bağlantısı "from" ile breadcrumb'da korunur. Sayfa gövdesi tek kaydırmadır (DataTable yok; kart içi kaydırma yok).
const Page = styled.div`
  min-height: 0;
`;

export default function PerformanceProfilePage() {
  const { t } = useTranslation();
  const { employeeId } = useParams();
  const location = useLocation();
  const history = useHistory();
  const query = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const periodId = query.get('periodId') || '';
  const from = safeFrom(query.get('from'));

  const [state, setState] = useState({ loading: true, error: null, data: null });
  const [attempt, setAttempt] = useState(0);
  // Başka ekranlardan belirli sekmeyle açılabilir (?tab=competencies|meetings|…); görünmeyen sekme aşağıda özete düşer.
  const [tab, setTab] = useState(() => query.get('tab') || 'summary');
  const [drawer, setDrawer] = useState(null);
  const [summary, setSummary] = useState(null);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    let alive = true;
    setState((s) => ({ ...s, loading: true, error: null }));
    fetchProfile(employeeId, periodId).then((r) => {
      if (!alive) return;
      setState({ loading: false, error: r.error || null, data: r.data || null });
    });
    return () => { alive = false; };
  }, [employeeId, periodId, attempt]);

  // Dönem/kişi değişince eski özet gösterilmez (başka kanıta aittir).
  useEffect(() => { setSummary(null); }, [employeeId, periodId]);

  const profile = state.data;
  const tabs = useMemo(() => profileTabs(profile?.viewer).map((k) => ({ key: k, label: t(`performance.profile.tabs.${k}`) })), [profile?.viewer, t]);
  useEffect(() => {
    if (!tabs.some((x) => x.key === tab)) setTab('summary');
  }, [tabs, tab]);

  const changePeriod = useCallback((id) => {
    const p = new URLSearchParams(location.search);
    if (id) p.set('periodId', id); else p.delete('periodId');
    history.replace({ pathname: location.pathname, search: `?${p.toString()}` });
  }, [history, location.pathname, location.search]);

  const generate = useCallback(async () => {
    if (!profile?.periodId || generating) return;
    setGenerating(true);
    const result = await summarizeProfile(employeeId, profile.periodId);
    setGenerating(false);
    if (result) setSummary(result);
  }, [employeeId, profile?.periodId, generating]);

  const crumbs = [
    from ? { label: t('performance.profile.back'), path: from } : { label: t('performance.profile.root'), plain: true },
    { label: profile?.employee?.name || t('performance.profile.title') },
  ];

  let body;
  if (state.loading && !profile) body = <State data-state="loading">{t('performance.profile.loading')}</State>;
  else if (state.error === 'forbidden') body = <State data-state="forbidden">{t('performance.profile.forbiddenText')}</State>;
  else if (state.error) {
    body = (
      <State data-state="error">
        <span>{t('performance.operations.panel.error')}</span>
        <Button variant="outline" onClick={() => setAttempt((a) => a + 1)} data-action="retry">{t('performance.operations.panel.retry')}</Button>
      </State>
    );
  } else if (profile) {
    body = (
      <>
        <ProfileHeader profile={profile} onPeriodChange={changePeriod} onOpenBreakdown={() => setDrawer({ type: 'breakdown' })} />
        {!profile.periodId ? <State data-state="no-period">{t('performance.profile.noPeriod')}</State> : null}
        {profile.periodId && profile.viewer.aiAvailable ? (
          <Grid style={{ marginBottom: 16 }}>
            <AiSummaryPanel available summary={summary} generating={generating} onGenerate={generate} />
          </Grid>
        ) : null}
        {profile.periodId ? (
          <>
            <Tabs tabs={tabs} activeTab={tab} onChange={setTab} />
            <div data-tab={tab}>
              {tab === 'summary' ? (
                <SummaryTab
                  profile={profile}
                  onOpenTimeline={(entry) => setDrawer({ type: 'timeline', entry })}
                  onOpenAction={(action) => setDrawer({ type: 'action', action })}
                />
              ) : null}
              {tab === 'goals' ? <GoalsTab profile={profile} onOpenGoal={(goal) => setDrawer({ type: 'goal', goal })} /> : null}
              {tab === 'competencies' ? <CompetenciesTab profile={profile} /> : null}
              {tab === 'reviews' ? <ReviewsTab profile={profile} /> : null}
              {tab === 'meetings' ? <MeetingsTab profile={profile} /> : null}
              {tab === 'development' ? <DevelopmentTab profile={profile} /> : null}
              {tab === 'talent' ? <TalentTab profile={profile} /> : null}
              {tab === 'history' ? <HistoryTab profile={profile} /> : null}
            </div>
          </>
        ) : null}
        <EvidenceDrawer open={!!drawer} item={drawer} profile={profile} onClose={() => setDrawer(null)} />
      </>
    );
  }

  return (
    <Page>
      <Breadcrumbs items={crumbs} />
      {body}
    </Page>
  );
}
